import { useRef, useCallback, useState } from 'react';

interface SwipeGestureOptions {
  onSwipeRight?: () => void;
  onSwipeLeft?: () => void;
  threshold?: number;
}

export const useSwipeGesture = ({
  onSwipeRight,
  onSwipeLeft,
  threshold = 100
}: SwipeGestureOptions) => {
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const isDragging = useRef<boolean>(false);
  const [dragX, setDragX] = useState<number>(0);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [isTouching, setIsTouching] = useState<boolean>(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = true;
    setIsTouching(true);
    setDragX(0);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - touchStartX.current;
    const deltaY = Math.abs(currentY - touchStartY.current);
    
    // Only allow swipe left for completion, with elastic resistance beyond threshold
    let clampedDeltaX = Math.min(0, deltaX);
    
    // Add elastic resistance when exceeding threshold
    if (Math.abs(clampedDeltaX) > threshold) {
      const excess = Math.abs(clampedDeltaX) - threshold;
      const resistance = Math.sqrt(excess) * 0.3; // Elastic resistance formula
      clampedDeltaX = -(threshold + resistance);
    }
    
    setDragX(clampedDeltaX);
    
    // Prevent vertical scrolling if horizontal swipe is detected
    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > 5) {
      e.preventDefault();
    }
  }, [threshold]);

  const handleTouchEnd = useCallback(async (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = Math.abs(touchEndY - touchStartY.current);
    
    isDragging.current = false;
    setIsTouching(false);
    
    // Only trigger swipe if horizontal movement is greater than vertical
    if (Math.abs(deltaX) > deltaY && Math.abs(deltaX) > threshold) {
      if (deltaX > 0 && onSwipeRight) {
        onSwipeRight();
        setDragX(0);
      } else if (deltaX < 0 && onSwipeLeft) {
        setIsCompleting(true);
        // Animate to full left before completion
        setDragX(-400);
        try {
          setTimeout(async () => {
            try {
              await onSwipeLeft();
              setIsCompleting(false);
              setIsCompleted(true);
              // Keep dragX at -400 to hide completed task
            } catch (error) {
              console.error('Error completing task:', error);
              setIsCompleting(false);
              setDragX(0); // Reset on error
            }
          }, 300);
        } catch (error) {
          console.error('Error during swipe completion:', error);
          setIsCompleting(false);
          setDragX(0);
        }
        return;
      }
    }
    
    // Bounce back animation if threshold not met
    setDragX(0);
  }, [onSwipeRight, onSwipeLeft, threshold]);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    dragX,
    isCompleting,
    isCompleted,
    isTouching
  };
};